import { useCallback, useEffect, useRef, useState } from 'react';
import { listAgents } from '../api/agent';

/**
 * Lazy, paginated agent-summary loader.
 *
 * Fetches nothing until `enabled` is true (e.g. a dropdown opens), then pulls the
 * first page of `firstPageSize` rows; `loadMore()` appends `nextPageSize` rows at
 * a time. Only the fields returned by `mapRow` are kept in memory — by default
 * `{ id, name }`, the bare minimum the pickers need. This replaces the old
 * "page through every agent" (`listAllAgentSummaries`) pattern that made large
 * accounts slow.
 *
 * Mirrors the proven loadPage logic in `components/agent/AgentLoadDialog.js`.
 *
 * @param {Object} opts
 * @param {boolean} [opts.enabled=true]     When false, holds empty state and fetches nothing.
 * @param {string}  [opts.search]           Already-resolved (debounced) search string, or undefined.
 * @param {'all'|'name'|'model'} [opts.searchField='all']
 * @param {number}  [opts.firstPageSize=20] Rows on the first page.
 * @param {number}  [opts.nextPageSize=10]  Rows per `loadMore()`.
 * @param {Iterable<string>} [opts.excludeIds] Agent ids to drop from every page (client-side).
 * @param {(row:Object)=>Object} [opts.mapRow] Row projector; default keeps `{ id, name }`.
 * @returns {{agents:Array, loading:boolean, loadingMore:boolean, hasLoaded:boolean, hasMore:boolean, error:(string|null), loadMore:()=>void, reload:()=>void}}
 */
export default function useLazyAgents({
  enabled = true,
  search,
  searchField = 'all',
  firstPageSize = 20,
  nextPageSize = 10,
  excludeIds,
  mapRow,
} = {}) {
  const [agents, setAgents] = useState([]);
  /** Next page offset from the API (`null` when there are no more pages). */
  const [nextOffset, setNextOffset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  // False until the first page settles after `enabled` turns true. Lets callers
  // show a spinner (rather than an "empty" flash) in the frame between the
  // control opening and the fetch effect running.
  const [hasLoaded, setHasLoaded] = useState(false);

  // Monotonic request id: a response is only applied if it is the newest request,
  // so rapid enable/search/loadMore churn can't let a stale page overwrite a fresh
  // one.
  const reqIdRef = useRef(0);

  // Keep the latest excludeIds / mapRow in refs so loadPage's identity depends
  // only on primitive inputs (append pagination stays stable across renders).
  const excludeSetRef = useRef(null);
  excludeSetRef.current =
    excludeIds instanceof Set ? excludeIds : excludeIds ? new Set(excludeIds) : null;
  const mapRowRef = useRef(mapRow);
  mapRowRef.current = mapRow || ((row) => ({ id: row?.id, name: row?.name }));

  const loadPage = useCallback(
    async (startOffset, append) => {
      const reqId = ++reqIdRef.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        // Keep paging while a whole page is filtered out by `excludeIds` — else
        // the consumer would see a spuriously empty list (and, with nothing to
        // scroll, infinite-scroll couldn't advance past it). Stops as soon as a
        // page yields a surviving row, the server returns an empty page, or there
        // are no more pages, so it always terminates.
        let offset = startOffset;
        let survivors = [];
        let next = null;
        for (;;) {
          const data = await listAgents({
            limit: append ? nextPageSize : firstPageSize,
            offset,
            search: search || undefined,
            searchField,
          });
          // A newer request superseded this one — drop the result.
          if (reqId !== reqIdRef.current) return;
          const raw = Array.isArray(data?.agents) ? data.agents : [];
          const exclude = excludeSetRef.current;
          const kept = exclude && exclude.size ? raw.filter((a) => a?.id && !exclude.has(a.id)) : raw;
          survivors = kept.map((a) => mapRowRef.current(a));
          next = data?.next === false || data?.next == null ? null : data.next;
          if (survivors.length > 0 || raw.length === 0 || next == null) break;
          offset = next;
        }
        setAgents((prev) => (append ? [...prev, ...survivors] : survivors));
        setNextOffset(next);
      } catch (err) {
        if (reqId !== reqIdRef.current) return;
        const msg = err?.response?.data?.message || err?.message || 'Failed to load agents';
        setError(msg);
        if (!append) {
          setAgents([]);
          setNextOffset(null);
        }
      } finally {
        if (reqId === reqIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
          if (!append) setHasLoaded(true);
        }
      }
    },
    [firstPageSize, nextPageSize, search, searchField],
  );

  // (Re)load the first page when enabled, or whenever the search terms change
  // while enabled. When disabled, reset so the next enable refetches fresh
  // (also invalidates any in-flight request via the reqId bump).
  useEffect(() => {
    if (!enabled) {
      reqIdRef.current++;
      setAgents([]);
      setNextOffset(null);
      setLoading(false);
      setLoadingMore(false);
      setError(null);
      setHasLoaded(false);
      return;
    }
    loadPage(0, false);
  }, [enabled, loadPage]);

  const loadMore = useCallback(() => {
    if (nextOffset == null || loading || loadingMore) return;
    loadPage(nextOffset, true);
  }, [nextOffset, loading, loadingMore, loadPage]);

  const reload = useCallback(() => {
    loadPage(0, false);
  }, [loadPage]);

  return {
    agents,
    loading,
    loadingMore,
    hasLoaded,
    hasMore: nextOffset != null,
    error,
    loadMore,
    reload,
  };
}
